/**
 * API Keys Table Component
 *
 * Displays API key details in a table format with regenerate actions
 * Shows all rotation columns: Client ID, Client Secret, App Identifier, Status, Expires On, Created On, Updated On, Action
 */

import React, { useState, useMemo } from 'react';
import { Table, Button, type TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '../../../constants/pagination.constants';
import type { ApiKeysTableProps, ApiKeyData, ApiKeyStatus } from '../../../types/settings';


/**
 * Get status badge color classes
 */
const getStatusBadgeClasses = (status: ApiKeyStatus | undefined): string => {
  const baseClasses = 'inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide';

  switch (status) {
    case 'ACTIVE':
      return `${baseClasses} bg-green-100 text-green-800 border border-green-300`;
    case 'INACTIVE':
      return `${baseClasses} bg-gray-100 text-gray-600 border border-gray-300`;
    case 'GRACE':
      return `${baseClasses} bg-yellow-100 text-yellow-800 border border-yellow-300`;
    case 'EXPIRING_SOON':
      return `${baseClasses} bg-orange-100 text-orange-800 border border-orange-300`;
    default:
      return `${baseClasses} bg-green-100 text-green-800 border border-green-300`;
  }
};

/**
 * Get display status text
 */
const getDisplayStatus = (status: ApiKeyStatus | undefined): string => {
  if (status === 'EXPIRING_SOON') return 'EXPIRING SOON';
  return status || 'ACTIVE';
};


/**
 * Check if regenerate should be enabled
 */
const canRegenerate = (row: ApiKeyData): boolean => {
  // Cannot regenerate grace period keys or inactive keys
  return row.status !== 'GRACE' && row.status !== 'INACTIVE';
};

export const ApiKeysTable: React.FC<ApiKeysTableProps> = ({
  data,
  onRegenerate,
  showActions,
  isLoading = false,
}) => {
  // Pagination state (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Define table columns - always show all rotation columns
  const columns: TableColumn<ApiKeyData>[] = useMemo(() => {
    const baseColumns: TableColumn<ApiKeyData>[] = [
      {
        key: 'client_id',
        header: 'Client ID',
        accessor: (row) => row.client_id,
        width: 280,
        copyable: true,
      },
      {
        key: 'client_secret',
        header: 'Client Secret',
        accessor: (row) => row.client_secret || '-',
        width: 280,
        copyable: true,
      },
      {
        key: 'appIdentifier',
        header: 'App Identifier',
        accessor: (row) => row.appIdentifier,
        width: 180,
      },
      {
        key: 'status',
        header: 'Status',
        width: 200,
        render: (_value, row) => (
          <span className={getStatusBadgeClasses(row.status)}>
            {getDisplayStatus(row.status)}
          </span>
        ),
      },
      {
        key: 'expires_at',
        header: 'Expires On',
        accessor: (row) => row.expires_at || '-',
        width: 200,
      },
      {
        key: 'createdAt',
        header: 'Created On',
        accessor: (row) => row.createdAt || '-',
        width: 200,
      },
      {
        key: 'updatedAt',
        header: 'Updated On',
        accessor: (row) => row.updatedAt || '-',
        width: 200,
      },
    ];

    // Add actions column only if showActions is true
    if (showActions) {
      baseColumns.push({
        key: 'actions',
        header: 'Action',
        width: 130,
        align: 'center',
        render: (_value, row) => {
          const disabled = isLoading || !canRegenerate(row);
          let title = 'Regenerate API Key';
          if (row.status === 'GRACE') {
            title = 'Cannot regenerate key in grace period';
          } else if (row.status === 'INACTIVE') {
            title = 'Cannot regenerate inactive key';
          }

          return (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onRegenerate(row)}
              disabled={disabled}
              title={title}
            >
              Regenerate
            </Button>
          );
        },
      });
    }

    return baseColumns;
  }, [showActions, onRegenerate, isLoading]);

  return (
    <Table
      className="shadow-sm rounded-xl"
      columns={columns}
      data={data}
      selectable={false}
      loading={isLoading}
      pagination={{
        currentPage,
        pageSize,
        totalItems: data.length,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
    />
  );
};
